import React, { useState } from 'react';
import { Division, Goalie } from '../../common/interfaces';
import { CompactTable } from "../../common/components/CompactTable";
import { getGoalieDisplayName, getDivisionName } from "../../common/utils";

const validDivisions = Object.values(Division)
  .filter(v => typeof v === 'number' && v !== Division.UNKNOWN) as Division[];

interface GoalieRow {
  goalie: Goalie;
  eligibleDivisions: Division[];
  dirty: boolean;
}

export function GoalieDivisionTable(props: {
  goalies?: Goalie[];
}) {
  const [rows, setRows] = useState<GoalieRow[]>(
  (props.goalies || [])
    .filter(g => g.active === true)
    .map(g => ({
      goalie: g,
      eligibleDivisions: (g.eligibleDivisions ?? []).map(d =>
        typeof d === 'number' ? d : Division[d as keyof typeof Division]
      ).filter(d => d !== undefined) as Division[],
      dirty: false,
    }))
    .sort((a, b) => a.goalie.firstName.localeCompare(b.goalie.firstName))
  );
  const [saving, setSaving] = useState(false);

  const handleCheckbox = (goalieIndex: number, division: Division) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== goalieIndex) return row;
      const already = row.eligibleDivisions.includes(division);
      return {
        ...row,
        eligibleDivisions: already
          ? row.eligibleDivisions.filter(d => d !== division)
          : [...row.eligibleDivisions, division],
        dirty: true,
      };
    }));
  };

const handleSave = async () => {
  const dirtyRows = rows.filter(r => r.dirty);
  if (dirtyRows.length === 0) return;

  setSaving(true);
  try {
    await Promise.all(
      dirtyRows.map(row => {
        const params = new URLSearchParams();
        params.append('id', String(row.goalie.id))
        if (row.eligibleDivisions.length === 0) {
          params.append('divisions', String(Division.UNKNOWN));
        } else {
          row.eligibleDivisions.forEach(d => params.append('divisions', String(d)));
        }
        return fetch('/api/goalies', {
          method: 'POST',
          body: params,
        });
      })
    );
    setRows(prev => prev.map(row => ({ ...row, dirty: false })));
  } finally {
    setSaving(false);
  }
};
  const columns = [
    {
      title: 'Goalie',
      key: 'name',
      render: (_: any, row: GoalieRow) => {
        const hasNoDivisions = row.eligibleDivisions.length === 0;
        return (
          <span style={{
            fontWeight: row.dirty ? 'bold' : 'normal',
            color: hasNoDivisions ? 'red' : undefined,
          }}>
            {getGoalieDisplayName(row.goalie)}
          </span>
        );
      },
    },
    ...validDivisions.map(division => ({
      title: getDivisionName(division),
      key: division,
      render: (_: any, row: GoalieRow, index: number) => (
        <input
          type="checkbox"
          checked={row.eligibleDivisions.includes(division)}
          onChange={() => handleCheckbox(index, division)}
        />
      ),
    })),
  ];

  const dirtyCount = rows.filter(r => r.dirty).length;

  return (
    <div>
        <CompactTable
            data={rows}
            columns={columns}
            rowKey="goalie.id"
        />
        <div style={{ textAlign: 'right', marginTop: 8 }}>
            <button onClick={handleSave} disabled={saving || dirtyCount === 0}>
                {saving ? 'Saving...' : dirtyCount > 0 ? `Save (${dirtyCount} changed)` : 'Save'}
            </button>
        </div>
    </div>
  );
}